import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { KOLProfile } from '../../types';
import { kolUrlSegment } from '../../lib/slug';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Select from '../../components/ui/Select';

export default function KOLDataTools() {
  const navigate = useNavigate();
  const [agencyOrgId, setAgencyOrgId] = useState('');
  const [kols, setKols] = useState<KOLProfile[]>([]);
  const [dupes, setDupes] = useState<{ key: string; kolIds: string[] }[]>([]);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compareRows, setCompareRows] = useState<any[]>([]);
  const [keepId, setKeepId] = useState('');
  const [mergeId, setMergeId] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    (async () => {
      const me = await api.get('/auth/me');
      const agency = me.data.memberships?.find((m: any) => m.organization?.type === 'AGENCY');
      const oid = agency?.organizationId || '';
      setAgencyOrgId(oid);
      if (oid) {
        const [kolsRes, d] = await Promise.all([
          api.get('/kols', { params: { agencyOrgId: oid } }),
          api.get('/kols/duplicates', { params: { agencyOrgId: oid } }),
        ]);
        setKols(kolsRes.data);
        setDupes(d.data.duplicates || []);
      }
    })();
  }, []);

  const runCompare = async () => {
    if (!agencyOrgId || !compareA || !compareB || compareA === compareB) return;
    const res = await api.get('/kols/compare', {
      params: { agencyOrgId, ids: `${compareA},${compareB}` },
    });
    setCompareRows(res.data.kols || []);
  };

  const runMerge = async () => {
    if (!keepId || !mergeId || keepId === mergeId) return;
    if (!confirm('Merge will delete the merged profile and move portfolio/tags. Continue?')) return;
    try {
      setMerging(true);
      await api.post('/kols/merge', { keepId, mergeId });
      alert('Merged successfully');
      setMergeId('');
      const kolsRes = await api.get('/kols');
      setKols(kolsRes.data);
      if (agencyOrgId) {
        const d = await api.get('/kols/duplicates', { params: { agencyOrgId } });
        setDupes(d.data.duplicates || []);
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Merge failed (profiles may have active invitations)');
    } finally {
      setMerging(false);
    }
  };

  const kolOptions = kols.map((k) => (
    <option key={k.id} value={k.id}>
      {k.name} {k.email ? `(${k.email})` : ''}
    </option>
  ));

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">
          KOL data tools
        </h1>
        <p className="text-glass-text-2 text-sm mt-1">
          Duplicates, side-by-side compare, and merge (same agency only).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Possible duplicates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {dupes.length === 0 && (
            <p className="text-glass-text-3">No duplicate keys detected.</p>
          )}
          {dupes.map((d) => (
            <div key={d.key} className="p-3 rounded-xl border border-glass bg-glass-2">
              <div className="font-mono text-xs text-glass-text-3 mb-2">{d.key}</div>
              <div className="flex flex-wrap gap-2">
                {d.kolIds.map((id) => {
                  const k = kols.find((x) => x.id === id);
                  return (
                    <Button
                      key={id}
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        k ? navigate(`/agency/kols/${kolUrlSegment(k)}`) : navigate(`/agency/kols/${id}`)
                      }
                    >
                      {k?.name || id.slice(0, 8)}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Compare two KOLs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-glass-text-3 block mb-1">Profile A</label>
              <Select value={compareA} onChange={(e) => setCompareA(e.target.value)}>
                <option value="">Select…</option>
                {kolOptions}
              </Select>
            </div>
            <div>
              <label className="text-xs text-glass-text-3 block mb-1">Profile B</label>
              <Select value={compareB} onChange={(e) => setCompareB(e.target.value)}>
                <option value="">Select…</option>
                {kolOptions}
              </Select>
            </div>
          </div>
          <Button type="button" onClick={runCompare} disabled={!compareA || !compareB}>
            Load comparison
          </Button>
          {compareRows.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-glass">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-glass-3 text-glass-text-2 uppercase tracking-[0.05em] text-xs">
                    <th className="p-2 text-left font-medium">Field</th>
                    {compareRows.map((k) => (
                      <th key={k.id} className="p-2 text-left font-medium">
                        {k.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {['email', 'phone', 'locationCity', 'ethnicity', 'instagramHandle', 'tiktokHandle', 'avgPostRate'].map(
                    (field) => (
                      <tr key={field} className="border-t border-glass">
                        <td className="p-2 font-medium text-glass-text-2">{field}</td>
                        {compareRows.map((k) => (
                          <td key={k.id} className="p-2 text-glass-text">
                            {String((k as any)[field] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    )
                  )}
                  <tr className="border-t border-glass">
                    <td className="p-2 font-medium text-glass-text-2">Tags</td>
                    {compareRows.map((k) => (
                      <td key={k.id} className="p-2 text-glass-text">
                        {(k.tags || []).map((t: any) => t.tag?.name).filter(Boolean).join(', ') || '—'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Merge profiles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-amber-500 dark:text-amber-300 bg-amber-400/10 border border-amber-400/30 p-3 rounded-xl">
            Keeps the first profile, moves portfolio/tags from the second, then deletes the
            second. Fails if the merged profile is referenced in ways Prisma cannot cascade—
            resolve invitations first if needed.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-glass-text-3 block mb-1">Keep (surviving)</label>
              <Select value={keepId} onChange={(e) => setKeepId(e.target.value)}>
                <option value="">Select…</option>
                {kolOptions}
              </Select>
            </div>
            <div>
              <label className="text-xs text-glass-text-3 block mb-1">
                Merge into keep (will be removed)
              </label>
              <Select value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
                <option value="">Select…</option>
                {kolOptions}
              </Select>
            </div>
          </div>
          <Button variant="destructive" onClick={runMerge} disabled={merging || !keepId || !mergeId || keepId === mergeId}>
            {merging ? 'Merging…' : 'Merge profiles'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
